import React, { useState, useEffect, useContext } from 'react';
import { useTranslation } from 'react-i18next';
import axios from 'axios';
import { AppContext } from '../../App';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { FaDownload, FaFileInvoice, FaShoppingCart, FaCrown, FaEye } from 'react-icons/fa';
import { Link } from 'react-router-dom';
import { formatPrice } from '../../utils/currency';

export default function MyInvoices() {
  const { i18n } = useTranslation();
  const { API, user } = useContext(AppContext);
  const [invoices, setInvoices] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState('all'); // 'all', 'order', 'subscription'

  useEffect(() => {
    if (user) {
      fetchInvoices();
    }
  }, [user]);

  const fetchInvoices = async () => {
    try {
      const response = await axios.get(`${API}/invoices/my-invoices?user_id=${user.id}`);
      setInvoices(response.data);
      setLoading(false);
    } catch (error) {
      console.error('Error fetching invoices:', error);
      setLoading(false);
    }
  };

  const handleDownload = async (invoice) => {
    try {
      const response = await axios.get(
        `${API}/invoices/download/${invoice.id}?user_id=${user.id}`,
        { responseType: 'blob' }
      );
      
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `facture_${invoice.invoice_number}.pdf`);
      document.body.appendChild(link);
      link.click();
      link.remove();
    } catch (error) {
      console.error('Error downloading invoice:', error);
    }
  };

  const filteredInvoices = invoices.filter(inv => {
    if (filter === 'all') return true;
    return inv.type === filter;
  });

  if (loading) {
    return (
      <div className="min-h-screen bg-[#F5F5F5] flex items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-[#0A2540]"></div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#F5F5F5]">
      <div className="container mx-auto px-4 py-8">
        <div className="flex flex-wrap items-center justify-between gap-4 mb-8">
          <h1 className="text-3xl md:text-4xl font-bold text-[#0A2540]">
            {i18n.language === 'fr' ? '📄 Mes Factures' : '📄 My Invoices'}
          </h1>
          
          {/* Filter buttons */}
          <div className="flex gap-2">
            <Button 
              variant={filter === 'all' ? 'default' : 'outline'}
              size="sm"
              onClick={() => setFilter('all')}
              className={filter === 'all' ? 'bg-[#0A2540]' : ''}
            >
              {i18n.language === 'fr' ? 'Toutes' : 'All'}
            </Button>
            <Button 
              variant={filter === 'order' ? 'default' : 'outline'}
              size="sm"
              onClick={() => setFilter('order')}
              className={filter === 'order' ? 'bg-[#635BFF]' : ''}
            >
              <FaShoppingCart className="mr-2" />
              {i18n.language === 'fr' ? 'Commandes' : 'Orders'}
            </Button>
            <Button 
              variant={filter === 'subscription' ? 'default' : 'outline'}
              size="sm"
              onClick={() => setFilter('subscription')}
              className={filter === 'subscription' ? 'bg-amber-500' : ''}
            >
              <FaCrown className="mr-2" />
              {i18n.language === 'fr' ? 'Abonnements' : 'Subscriptions'}
            </Button>
          </div>
        </div>

        {filteredInvoices.length === 0 ? (
          <Card className="p-12 text-center">
            <FaFileInvoice className="text-6xl text-gray-300 mx-auto mb-4" />
            <p className="text-gray-500 text-lg">
              {i18n.language === 'fr' 
                ? 'Aucune facture disponible' 
                : 'No invoices available'}
            </p>
            <p className="text-gray-400 text-sm mt-2">
              {i18n.language === 'fr' 
                ? 'Les factures apparaîtront ici après vos achats ou abonnements' 
                : 'Invoices will appear here after your purchases or subscriptions'}
            </p>
          </Card>
        ) : (
          <div className="space-y-4">
            {filteredInvoices.map(invoice => (
              <Card key={invoice.id} className="hover:shadow-md transition-shadow">
                <CardContent className="p-6">
                  <div className="flex flex-col md:flex-row justify-between items-start md:items-center gap-4">
                    <div className="flex-1">
                      <div className="flex items-center gap-3 mb-2">
                        {invoice.type === 'order' ? (
                          <div className="p-2 bg-[#635BFF]/10 rounded-lg">
                            <FaShoppingCart className="text-[#635BFF]" />
                          </div>
                        ) : (
                          <div className="p-2 bg-amber-100 rounded-lg">
                            <FaCrown className="text-amber-600" />
                          </div>
                        )}
                        <div>
                          <h3 className="font-bold text-lg text-[#0A2540]">
                            {invoice.invoice_number}
                          </h3>
                          <p className="text-sm text-gray-600">
                            {invoice.description}
                          </p>
                        </div>
                      </div>
                      
                      <div className="flex flex-wrap gap-4 text-sm mt-3">
                        <span className="text-gray-500">
                          📅 {new Date(invoice.created_at).toLocaleDateString(i18n.language === 'fr' ? 'fr-FR' : 'en-US', {
                            day: 'numeric',
                            month: 'long',
                            year: 'numeric'
                          })}
                        </span>
                        <span className="font-bold text-[#0A2540]">
                          {formatPrice(invoice.amount, 'EUR')}
                        </span>
                        <Badge className={
                          invoice.status === 'paid' 
                            ? 'bg-green-100 text-green-700' 
                            : 'bg-orange-100 text-orange-700'
                        }>
                          {invoice.status === 'paid' 
                            ? (i18n.language === 'fr' ? '✓ Payé' : '✓ Paid')
                            : (i18n.language === 'fr' ? 'En attente' : 'Pending')}
                        </Badge>
                        <Badge variant="outline" className="text-gray-500">
                          {invoice.type === 'order' 
                            ? (i18n.language === 'fr' ? 'Commande' : 'Order')
                            : (i18n.language === 'fr' ? 'Abonnement' : 'Subscription')}
                        </Badge>
                      </div>
                    </div>
                    
                    <div className="flex gap-2">
                      {invoice.type === 'order' && invoice.order_id && (
                        <Link to={`/user/orders/${invoice.order_id}`}>
                          <Button variant="outline" size="sm">
                            <FaEye className="mr-2" />
                            {i18n.language === 'fr' ? 'Voir commande' : 'View order'}
                          </Button>
                        </Link>
                      )}
                      <Button 
                        onClick={() => handleDownload(invoice)} 
                        className="bg-[#0A2540] hover:bg-[#0A2540]/90"
                      >
                        <FaDownload className="mr-2" />
                        {i18n.language === 'fr' ? 'Télécharger PDF' : 'Download PDF'}
                      </Button>
                    </div>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        )}
        
        {/* Summary */}
        {invoices.length > 0 && (
          <Card className="mt-8 bg-gradient-to-r from-[#0A2540] to-[#1a3a5c] text-white">
            <CardContent className="p-6">
              <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                <div className="text-center">
                  <p className="text-2xl font-bold">{invoices.length}</p>
                  <p className="text-sm opacity-80">
                    {i18n.language === 'fr' ? 'Total factures' : 'Total invoices'}
                  </p>
                </div>
                <div className="text-center">
                  <p className="text-2xl font-bold">
                    {formatPrice(
                      invoices.filter(i => i.status === 'paid').reduce((sum, i) => sum + (i.amount || 0), 0),
                      'EUR'
                    )}
                  </p>
                  <p className="text-sm opacity-80">
                    {i18n.language === 'fr' ? 'Total payé' : 'Total paid'}
                  </p>
                </div>
                <div className="text-center">
                  <p className="text-2xl font-bold">
                    {formatPrice(
                      invoices.filter(i => i.status !== 'paid').reduce((sum, i) => sum + (i.amount || 0), 0),
                      'EUR'
                    )}
                  </p>
                  <p className="text-sm opacity-80">
                    {i18n.language === 'fr' ? 'En attente' : 'Pending'}
                  </p>
                </div>
              </div>
            </CardContent>
          </Card>
        )}
      </div>
    </div>
  );
}
